// Iter 133 — Shock Games of the Day carousel.
//
// Top-3 prestige-weighted banner cards rendered above the homepage hero.
// Layout inspired by the MLS Saturday Showdown poster format:
//   left player cutout  ·  team crests in center w/ "VS"  ·  right player cutout
//   match metadata + NDAKUBET prediction footer
//
// Visible to BOTH anonymous (landing page) and logged-in (homepage) visitors.
// Click → routes to /match/{fixture_id} if logged-in, else /auth.
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import {
  ChevronLeft, ChevronRight, Flame, Trophy, Sparkles, ArrowUpRight, Lock,
} from "lucide-react";

const AUTO_ROTATE_MS = 9000;

const fmtKickoff = (iso) => {
  if (!iso) return "TODAY";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    }).toUpperCase();
  } catch { return "TODAY"; }
};

const lastName = (fullName) => {
  if (!fullName) return "";
  const parts = fullName.split(/[\s.]+/).filter(Boolean);
  return parts[parts.length - 1].toUpperCase();
};

const ShockGamesCarousel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [games, setGames] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/shock-games/today`, { timeout: 12000 });
        if (!cancelled) {
          setGames(data?.games || []);
          // Iter 134e — accept either the playlist (preferred) or the
          // legacy single-sponsor field for back-compat.
          const list = Array.isArray(data?.sponsors) ? data.sponsors :
                       data?.sponsor ? [data.sponsor] : [];
          setSponsors(list);
        }
      } catch (e) {
        // Quiet fail — the carousel just doesn't render.
        if (!cancelled) {
          setGames([]);
          setSponsors([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-rotate
  useEffect(() => {
    if (paused || games.length <= 1) return;
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % games.length);
    }, AUTO_ROTATE_MS);
    return () => clearTimeout(timerRef.current);
  }, [index, paused, games.length]);

  if (loading) {
    return (
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6"
        data-testid="shock-games-loading"
      >
        <div className="aspect-[21/9] sm:aspect-[16/6] rounded-3xl bg-gradient-to-br from-slate-800 to-slate-900 animate-pulse" />
      </div>
    );
  }
  if (!games.length) return null;

  const game = games[index];
  const onBannerClick = () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (game.fixture_id) navigate(`/match/${game.fixture_id}`);
  };

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6"
      data-testid="shock-games-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex items-center gap-2 mb-3">
        <Flame className="w-4 h-4 text-amber-500" />
        <h2 className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-bold text-foreground">
          Shock Games of the Day
        </h2>
        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
          · Top {games.length} picks
        </span>
        <span className="ml-auto flex items-center gap-1.5">
          {games.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              data-testid={`shock-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index
                  ? "w-7 bg-foreground"
                  : "w-1.5 bg-muted-foreground/40 hover:bg-muted-foreground/70"
              }`}
              aria-label={`Banner ${i + 1}`}
            />
          ))}
        </span>
      </div>

      <Banner
        game={game}
        onClick={onBannerClick}
        isAnonymous={!user}
        sponsor={sponsors.length ? sponsors[index % sponsors.length] : null}
      />
    </section>
  );
};


// Track an impression + open the tracked redirect URL.  Falls back to
// the raw click_url if the beacon endpoint fails so we never lose a
// click conversion to a network blip.
// In-memory throttle: ignore repeat clicks on the same sponsor within
// 3s (defeats accidental double-clicks from inflating counters).
const _SPONSOR_CLICK_GUARD = new Map();
const handleSponsorClick = async (sponsor) => {
  if (!sponsor) return;
  const now = Date.now();
  const last = _SPONSOR_CLICK_GUARD.get(sponsor.id) || 0;
  if (now - last < 3000) {
    // Still open the URL — UX-wise the user expects a tab — but skip
    // the beacon so we don't inflate the counter.
    if (sponsor.click_url) {
      window.open(sponsor.click_url, "_blank", "noopener,noreferrer");
    }
    return;
  }
  _SPONSOR_CLICK_GUARD.set(sponsor.id, now);
  try {
    const { data } = await axios.post(
      `${API}/shock-games/sponsor/${sponsor.id}/click`,
      null,
      { timeout: 4000 }
    );
    const url = data?.redirect_url || sponsor.click_url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  } catch {
    if (sponsor.click_url) {
      window.open(sponsor.click_url, "_blank", "noopener,noreferrer");
    }
  }
};


const Banner = ({ game, onClick, isAnonymous, sponsor }) => {
  const home = game.home_team || {};
  const away = game.away_team || {};
  const homePlayer = game.home_player || null;
  const awayPlayer = game.away_player || null;
  const pred = game.prediction || {};
  const league = game.league || {};

  const conf = pred.confidence ? Math.round(pred.confidence) : null;
  const confTone =
    conf == null ? "text-muted-foreground"
    : conf >= 70 ? "text-emerald-400"
    : conf >= 55 ? "text-amber-400"
    : "text-rose-400";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`shock-banner-${game.fixture_id}`}
      className="group relative w-full aspect-[21/9] sm:aspect-[16/6] lg:aspect-[18/7] rounded-3xl overflow-hidden border border-border bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 text-left shadow-[0_24px_80px_-32px_rgba(0,0,0,0.7)] hover:shadow-[0_24px_80px_-24px_rgba(0,0,0,0.85)] transition-all"
    >
      {/* Stadium-vibe background mesh */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `
            radial-gradient(circle at 25% 50%, rgba(59,130,246,0.35) 0%, transparent 50%),
            radial-gradient(circle at 75% 50%, rgba(34,197,94,0.25) 0%, transparent 50%),
            radial-gradient(circle at 50% 100%, rgba(245,158,11,0.20) 0%, transparent 60%)
          `,
        }}
      />
      {/* Subtle grid/scanline overlay for stadium feel */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 6px)",
        }}
      />

      {/* LEFT PLAYER cutout */}
      <PlayerCutout side="left" player={homePlayer} team={home} />
      {/* RIGHT PLAYER cutout */}
      <PlayerCutout side="right" player={awayPlayer} team={away} />

      {/* Sponsor pill (Iter 134c → 134e) — top-right, optional.
          Fires an impression beacon on mount + uses tracked-click
          redirect to attribute traffic via UTM params. */}
      {sponsor?.name && (
        <SponsorPill sponsor={sponsor} />
      )}

      {/* CENTER stack — header + crests + VS */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4 sm:px-8">
        {/* League pill */}
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm">
          {league.logo && (
            <img src={league.logo} alt="" className="w-3.5 h-3.5 object-contain" />
          )}
          <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-bold text-white/90">
            {league.name || ""}
          </span>
        </div>

        {/* Title */}
        <div className="mt-2 sm:mt-3 text-center font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.05]"
             style={{ fontVariationSettings: '"opsz" 96' }}>
          Shock <span className="text-amber-400">Showdown</span>
        </div>

        {/* Crests + VS */}
        <div className="flex items-center gap-4 sm:gap-8 mt-3 sm:mt-5">
          <CrestPanel team={home} />
          <div className="flex flex-col items-center">
            <div className="font-display font-black text-2xl sm:text-3xl text-white/95"
                 style={{ fontVariationSettings: '"opsz" 96' }}>
              VS
            </div>
            {conf != null && (
              <div className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[10px] sm:text-xs font-bold ${confTone}`}>
                <Sparkles className="w-3 h-3" />
                {conf}% confidence
              </div>
            )}
          </div>
          <CrestPanel team={away} />
        </div>

        {/* Match info */}
        <div className="mt-3 sm:mt-4 text-center">
          <div className="font-display font-bold text-sm sm:text-base lg:text-lg text-white/95 tracking-tight">
            {home.name} <span className="text-white/40 font-medium">vs</span> {away.name}
          </div>
          <div className="mt-1 text-[10px] sm:text-xs font-tabular tracking-[0.16em] text-white/60 uppercase">
            {fmtKickoff(game.kickoff)}
          </div>
        </div>
      </div>

      {/* Footer ribbon — NDAKUBET prediction */}
      <div className="absolute bottom-0 inset-x-0 bg-black/55 backdrop-blur-sm border-t border-white/10 px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center font-bold text-[11px] text-black shrink-0">
            N
          </div>
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.18em] text-white/60 font-bold leading-tight">
              Predicted by NDAKUBET AI
            </div>
            <div className="text-xs sm:text-sm font-bold text-white/95 truncate">
              {pred.market}: <span className="text-emerald-400">{pred.pick}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isAnonymous ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[10px] sm:text-xs font-bold uppercase tracking-wider">
              <Lock className="w-3 h-3" />
              Log in to view
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-bold text-white/90 uppercase tracking-wider">
              See analysis
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          )}
        </div>
      </div>
    </button>
  );
};


// Player cutout — positioned absolutely on left/right with team-colour gradient at the bottom.
const PlayerCutout = ({ side, player, team }) => {
  if (!player?.photo && !player?.cutout_url) return null;
  const isLeft = side === "left";
  // Iter 145p — Prefer the AI-generated transparent cutout when available.
  // Fall back to the raw API-Sports headshot, which is opaque and
  // therefore needs the linear-gradient bottom-fade mask to blend into
  // the stadium backdrop.
  const hasCutout = Boolean(player?.cutout_url);
  const apiBase = (typeof window !== "undefined" && window.__BACKEND_URL__) || process.env.REACT_APP_BACKEND_URL || "";
  const src = hasCutout
    ? (player.cutout_url.startsWith("http") ? player.cutout_url : `${apiBase}${player.cutout_url}`)
    : player.photo;
  return (
    <div
      className={`absolute ${isLeft ? "left-0" : "right-0"} ${
        hasCutout
          ? "bottom-0 w-[36%] sm:w-[44%] md:w-[48%] lg:w-[44%] h-[98%] sm:h-[100%]"
          : "bottom-0 w-[30%] sm:w-[28%] lg:w-[24%] h-[88%] sm:h-[90%]"
      } pointer-events-none select-none`}
      style={hasCutout ? {} : {
        WebkitMaskImage:
          "linear-gradient(180deg, rgba(0,0,0,1) 75%, rgba(0,0,0,0) 100%)",
        maskImage:
          "linear-gradient(180deg, rgba(0,0,0,1) 75%, rgba(0,0,0,0) 100%)",
      }}
      data-testid={`shock-player-cutout-${side}`}
    >
      <img
        src={src}
        alt={player.name || team.name}
        className={`w-full h-full ${hasCutout ? "object-contain object-bottom drop-shadow-[0_8px_24px_rgba(0,0,0,0.45)]" : "object-cover object-top"}`}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
      />
      <div
        className={`absolute bottom-3 ${isLeft ? "left-3" : "right-3"} text-[10px] sm:text-xs font-display font-extrabold tracking-tight text-white/95 ${isLeft ? "" : "text-right"}`}
        style={{ textShadow: "0 1px 4px rgba(0,0,0,0.85)" }}
      >
        {lastName(player.name)}
      </div>
    </div>
  );
};

// Sponsor pill — fires an impression beacon on mount (one per
// sponsor id per session) + opens UTM-tracked URL on click.
const SponsorPill = ({ sponsor }) => {
  useEffect(() => {
    if (!sponsor?.id) return;
    const key = `ndaku_sponsor_imp_${sponsor.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    axios.post(
      `${API}/shock-games/sponsor/${sponsor.id}/impression`,
      null,
      { timeout: 4000 }
    ).catch(() => { /* quiet — beacon is best-effort */ });
  }, [sponsor?.id]);

  if (!sponsor?.name) return null;

  return (
    <div
      className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10"
      onClick={(e) => {
        e.stopPropagation();
        handleSponsorClick(sponsor);
      }}
      data-testid={`shock-sponsor-pill-${sponsor.id || "anon"}`}
    >
      <div
        className={`inline-flex items-center gap-1.5 sm:gap-2 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full bg-black/40 border border-white/20 backdrop-blur-md transition-all hover:bg-black/55 hover:border-white/40 ${
          sponsor.click_url ? "cursor-pointer" : "cursor-default"
        }`}
      >
        {sponsor.logo_url && (
          <img
            src={sponsor.logo_url}
            alt={sponsor.name}
            className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div className="leading-tight">
          <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.18em] font-bold text-white/60">
            {sponsor.tagline || "Brought to you by"}
          </div>
          <div className="text-[10px] sm:text-xs font-extrabold uppercase tracking-wide text-white">
            {sponsor.name}
          </div>
        </div>
      </div>
    </div>
  );
};


const CrestPanel = ({ team }) => (
  <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-md flex items-center justify-center p-2 shrink-0">
    {team?.logo ? (
      <img
        src={team.logo}
        alt={team.name}
        className="max-w-full max-h-full object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.4)]"
      />
    ) : (
      <Trophy className="w-8 h-8 text-white/60" />
    )}
  </div>
);

export default ShockGamesCarousel;